export interface NavigationItem {
  path: string;
  paths: string[];
  translationKey?: string;
  desktopLabel: string;
  mobileLabel?: string;
  icon: string;
  showInSidebar?: boolean;
  showInBottomNav?: boolean;
  requiredRoles?: string[];
}

// One list controls the labels and icons for both the desktop sidebar and the phone bottom bar.
export const APP_SIDEBAR_WIDTH = 240;

// These items are the main places a staff user can go in the app.
export const navigationItems: NavigationItem[] = [
  {
    path: '/',
    paths: ['/', '/dashboard'],
    translationKey: 'navigation.dashboard',
    desktopLabel: 'Dashboard',
    mobileLabel: 'Home',
    icon: '🏠',
    showInSidebar: true,
    showInBottomNav: true,
  },
  {
    path: '/clients',
    paths: ['/clients'],
    translationKey: 'navigation.clients',
    desktopLabel: 'Clients',
    icon: '👥',
    showInSidebar: true,
  },
  {
    path: '/projects',
    paths: ['/projects'],
    translationKey: 'navigation.projects',
    desktopLabel: 'Projects',
    mobileLabel: 'Project',
    icon: '🏗️',
    showInSidebar: true,
    showInBottomNav: true,
  },
  {
    path: '/project-control-centre',
    paths: ['/project-control-centre'],
    desktopLabel: 'Project Control Centre',
    icon: '🗓️',
    requiredRoles: ['pm', 'admin'],
    showInSidebar: true,
  },
  {
    path: '/quotations',
    paths: ['/quotations'],
    translationKey: 'navigation.quotations',
    desktopLabel: 'Quotations',
    mobileLabel: 'Quote',
    icon: '📋',
    showInSidebar: true,
    showInBottomNav: true,
    requiredRoles: ['finance'],
  },
  {
    path: '/invoices',
    paths: ['/invoices'],
    translationKey: 'navigation.invoices',
    desktopLabel: 'Invoices',
    mobileLabel: 'Invoice',
    icon: '🧾',
    showInSidebar: true,
    showInBottomNav: true,
  },
  {
    path: '/tasks',
    paths: ['/tasks'],
    translationKey: 'navigation.tasks',
    desktopLabel: 'Tasks',
    mobileLabel: 'Tasks',
    icon: '✅',
    showInSidebar: true,
    showInBottomNav: true,
  },
  {
    path: '/certificates',
    paths: ['/certificates'],
    translationKey: 'navigation.certificates',
    desktopLabel: 'Certificates',
    mobileLabel: 'Cert',
    icon: '🏆',
    showInSidebar: true,
    showInBottomNav: true,
  },
  {
    path: '/users',
    paths: ['/users'],
    translationKey: 'navigation.users',
    desktopLabel: 'Users',
    icon: '👤',
    showInSidebar: true,
  },
  {
    path: '/templates',
    paths: ['/templates'],
    translationKey: 'navigation.templates',
    desktopLabel: 'Templates',
    icon: '📄',
    showInSidebar: true,
  },
  {
    path: '/ai',
    paths: ['/ai', '/ai/admin', '/ai/email'],
    desktopLabel: 'Company AI',
    icon: '🧠',
    showInSidebar: true,
    requiredRoles: ['pm', 'finance', 'admin'],
  },
];

// Some pages belong in the desktop sidebar only, while others should also appear on phones.
export const sidebarNavigationItems = navigationItems.filter((item) => item.showInSidebar !== false);
export const bottomNavigationItems = navigationItems.filter((item) => item.showInBottomNav);

export function filterNavigationItemsForUser<T extends NavigationItem>(
  items: T[],
  canAccessItem: (item: T) => boolean
) {
  return items.filter((item) => canAccessItem(item));
}

// Make route matching forgiving so `/Projects` and `/projects/` still count as the same screen.
function normalizePathname(pathname: string) {
  const path = String(pathname || '').toLowerCase();
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
}

export function isNavigationItemActive(pathname: string, item: NavigationItem) {
  const normalizedPath = normalizePathname(pathname);

  return item.paths.some((path) => {
    const candidate = normalizePathname(path);

    if (candidate === '/') {
      return normalizedPath === '/';
    }

    return normalizedPath === candidate || normalizedPath.startsWith(`${candidate}/`);
  });
}
